import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  Badge,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { Download, ExpandMore, FileDownload } from '@mui/icons-material'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts'
import { apiService } from '../../services/api'
import debug from '../../utils/debug'
import GamifiedDistrictCard from './GamifiedDistrictCard'
import GamifiedRegionCard from './GamifiedRegionCard'
import ProductGroupSelector from '../ui/ProductGroupSelector'
import {
  ReportShell, HeaderButton, FilterBar, KpiStrip, Icon, fmt,
} from '../report-shell'

const OrangeDistrictAnalysis = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [analysisMode, setAnalysisMode] = useState('monthly')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedProductGroup, setSelectedProductGroup] = useState(null)
  const [data, setData] = useState(null)
  const [expandedDistrict, setExpandedDistrict] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [viewMode, setViewMode] = useState('gamified')

  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ]

  // Fetch Orange districts analysis data
  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // For yearly mode, pass null for month
      const monthParam = analysisMode === 'yearly' ? null : selectedMonth
      
      debug.log('OrangeDistrictAnalysis', `Fetching data for mode: ${analysisMode}, month: ${monthParam}, year: ${selectedYear}`)
      
      const response = await apiService.getOrangeDistrictsAnalysis(9999, monthParam, selectedYear, selectedProductGroup)
      const analysisData = response.data
      
      console.log('OrangeDistrictAnalysis - Data received:', analysisData)
      setData(analysisData)
      
    } catch (err) {
      debug.error('OrangeDistrictAnalysis', 'Error fetching data:', err)
      setError(`Błąd pobierania danych analizy dystryktów Orange: ${err.message}`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  // Export Districts Excel
  const exportDistrictsExcel = async () => {
    try {
      debug.log('OrangeDistrictAnalysis', 'Exporting Districts Excel')
      
      // For yearly mode, pass null for month
      const monthParam = analysisMode === 'yearly' ? null : selectedMonth
      
      const response = await apiService.getOrangeDistrictsExport(monthParam, selectedYear, selectedProductGroup)

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url

      // Generate filename
      const monthName = monthParam ? monthNames[monthParam - 1] : 'ROK'
      const groupSuffix = selectedProductGroup ? `_${selectedProductGroup}` : ''
      const filename = `Analiza_Dystrykty_Orange_${monthName}_${selectedYear}${groupSuffix}.xlsx`
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
    } catch (err) {
      debug.error('OrangeDistrictAnalysis', 'Error exporting Excel:', err)
      setError(`Błąd exportu Excel: ${err.message}`)
    }
  }

  // Load data when parameters change
  useEffect(() => {
    fetchData()
  }, [analysisMode, selectedMonth, selectedYear, selectedProductGroup])

  // Handle analysis mode change
  const handleAnalysisModeChange = (mode) => {
    setAnalysisMode(mode)
    if (mode === 'yearly') {
      setSelectedMonth(null)
    }
    // For monthly mode, selectedMonth is already set to current month by default
  }

  // Prepare all districts flattened for ranking
  const allDistricts = React.useMemo(() => {
    if (!data?.regions) return []
    
    const districts = []
    data.regions.forEach(region => {
      region.districts.forEach(district => {
        districts.push({
          ...district,
          region_name: region.region_name
        })
      })
    })
    
    return districts.sort((a, b) => b.total_sales - a.total_sales)
  }, [data])

  // Calculate gamification metrics
  const gamificationData = React.useMemo(() => {
    if (!allDistricts.length || !data) return { districts: [], regions: [], avgSales: 0 }
    
    // Calculate average sales
    const totalSales = allDistricts.reduce((sum, d) => sum + d.total_sales, 0)
    const avgSales = totalSales / allDistricts.length
    
    // Get max sales for progress bars
    const maxSales = allDistricts[0]?.total_sales || 1
    
    // Function to get badge based on performance
    const getBadge = (district, index) => {
      const salesPerSalon = district.total_sales / (district.salon_count || 1)
      const avgSalesPerSalon = avgSales / (allDistricts.reduce((sum, d) => sum + d.salon_count, 0) / allDistricts.length)
      
      if (index === 0) return { icon: '👑', label: 'Lider Absolutny', color: '#FFD700' }
      if (index < 3) return { icon: '🔥', label: 'Gorący Dystrykt', color: '#FF6B6B' }
      if (district.total_sales > avgSales * 1.5) return { icon: '⭐', label: 'Gwiazda', color: '#4ECDC4' }
      if (salesPerSalon > avgSalesPerSalon * 1.2) return { icon: '💎', label: 'Perfekcja', color: '#95E1D3' }
      if (district.total_sales > avgSales) return { icon: '📈', label: 'Powyżej Średniej', color: '#4CAF50' }
      if (district.total_sales > avgSales * 0.5) return { icon: '🎯', label: 'Solidny Gracz', color: '#FFA726' }
      return { icon: '💤', label: 'Potencjał', color: '#9E9E9E' }
    }
    
    // Function to get tier level
    const getTier = (sales, maxSales) => {
      const percent = (sales / maxSales) * 100
      if (percent >= 80) return { name: 'Platinum', color: '#E5E4E2', icon: '💎' }
      if (percent >= 60) return { name: 'Gold', color: '#FFD700', icon: '🥇' }
      if (percent >= 40) return { name: 'Silver', color: '#C0C0C0', icon: '🥈' }
      return { name: 'Bronze', color: '#CD7F32', icon: '🥉' }
    }
    
    // Function to get tempo icon
    const getTempo = (sales, avgSales) => {
      if (sales > avgSales * 1.5) return '🚀'
      if (sales > avgSales) return '⚡'
      if (sales > avgSales * 0.7) return '🏃'
      if (sales > avgSales * 0.3) return '🐢'
      return '💤'
    }
    
    // Enhance districts with gamification data
    const enhancedDistricts = allDistricts.map((district, index) => {
      const progressPercent = (district.total_sales / maxSales) * 100
      const vsAverage = ((district.total_sales / avgSales - 1) * 100).toFixed(1)
      const badge = getBadge(district, index)
      const tier = getTier(district.total_sales, maxSales)
      const tempo = getTempo(district.total_sales, avgSales)
      
      // Medal for TOP 3
      let medal = null
      if (index === 0) medal = '🥇'
      else if (index === 1) medal = '🥈'
      else if (index === 2) medal = '🥉'
      
      return {
        ...district,
        rank: index + 1,
        progressPercent,
        vsAverage,
        vsAverageNum: parseFloat(vsAverage),
        badge,
        tier,
        tempo,
        medal
      }
    })
    
    // Calculate region gamification
    const regionStats = data.regions.map((region, index) => {
      const regionMaxSales = data.regions[0]?.total_sales || 1
      const progressPercent = (region.total_sales / regionMaxSales) * 100
      const regionAvgSales = totalSales / data.regions.length
      const vsAverage = ((region.total_sales / regionAvgSales - 1) * 100).toFixed(1)
      
      let medal = null
      if (index === 0) medal = '🥇'
      else if (index === 1) medal = '🥈'
      else if (index === 2) medal = '🥉'
      
      const tier = getTier(region.total_sales, regionMaxSales)
      const tempo = getTempo(region.total_sales, regionAvgSales)
      
      return {
        ...region,
        rank: index + 1,
        progressPercent,
        vsAverage,
        vsAverageNum: parseFloat(vsAverage),
        tier,
        tempo,
        medal
      }
    })
    
    return {
      districts: enhancedDistricts,
      regions: regionStats,
      avgSales,
      maxSales
    }
  }, [allDistricts, data])

  // Prepare chart data
  const chartData = React.useMemo(() => {
    if (!allDistricts.length) return { topDistricts: [], regionalComparison: [] }
    
    // Top 20 districts
    const topDistricts = allDistricts.slice(0, 20).map((district, index) => ({
      name: `${district.district_name} (${district.region_name})`,
      fullName: district.district_name,
      region: district.region_name,
      sales: district.total_sales,
      index: index + 1
    }))

    // Regional comparison
    const regionalComparison = data.regions.map(region => ({
      region: region.region_name,
      sales: region.total_sales,
      districts: region.district_count
    }))

    return { topDistricts, regionalComparison }
  }, [allDistricts, data])

  // ─ FilterBar / KpiStrip config ──────────────────────────────
  const periods = [
    {
      type: 'segmented', key: 'analysisMode', label: 'Rodzaj analizy',
      value: analysisMode,
      options: [
        { value: 'monthly', label: 'Konkretny miesiąc' },
        { value: 'yearly',  label: 'Cały rok' },
      ],
      onChange: handleAnalysisModeChange,
    },
    {
      type: 'select', key: 'year', label: 'Rok',
      value: selectedYear, display: String(selectedYear),
      options: [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2]
        .map(y => ({ value: y, label: String(y) })),
      onChange: (v) => setSelectedYear(parseInt(v)),
      allowClear: false,
    },
    {
      type: 'select', key: 'month', label: 'Miesiąc',
      value: selectedMonth, display: selectedMonth ? monthNames[selectedMonth - 1] : '',
      options: monthNames.map((n, i) => ({ value: i + 1, label: n })),
      onChange: (v) => setSelectedMonth(parseInt(v)),
      allowClear: false,
      hideWhen: analysisMode !== 'monthly',
    },
  ]

  const kpiTiles = data ? [
    {
      key: 'sales',
      label: analysisMode === 'monthly' ? 'Sprzedaż w miesiącu' : 'Łączna sprzedaż',
      value: fmt.int(data.total_sales || 0),
      unit: 'szt.',
      sub: data.analysis_period,
    },
    { key: 'regions',   label: 'Liczba regionów',   value: fmt.int(data.total_regions || 0) },
    { key: 'districts', label: 'Liczba dystryktów', value: fmt.int(data.total_districts || 0) },
    {
      key: 'period', label: 'Okres analizy',
      value: data.analysis_period || '—',
    },
  ] : []

  return (
    <ReportShell
      title="Orange — Analiza dystryktów"
      subtitle="Agregacja sprzedaży i KPI per dystrykt OPL z gamifikowanym rankingiem regionów i dystryktów."
      breadcrumb={['KAM-SFA', 'Orange', 'Analiza dystryktów']}
      accentColor="#ff7900"
      actions={
        <HeaderButton primary onClick={exportDistrictsExcel} disabled={loading || !data}>
          <Icon.download /> Eksport Excel
        </HeaderButton>
      }
      filterBar={
        <FilterBar
          periods={periods}
          extraRows={[{
            label: 'Grupa produktów',
            fields: [{
              type: 'custom', key: 'productGroup',
              render: () => (
                <ProductGroupSelector
                  operatorCode="ORANGE"
                  value={selectedProductGroup}
                  onChange={setSelectedProductGroup}
                  showAll={true}
                />
              ),
            }],
          }]}
        />
      }
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data && (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      )}

      {data && (
        <>
          {/* Gamified Regional Ranking */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  🎮 Ranking Regionów
                </Typography>
              </Box>

              {gamificationData.avgSales > 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  🏆 <strong>Średnia sprzedaż:</strong> {gamificationData.avgSales.toFixed(0)} szt. | 
                  <strong> Max sprzedaż:</strong> {gamificationData.maxSales?.toLocaleString() || 0} szt.
                </Alert>
              )}

              <Grid container spacing={3}>
                {gamificationData.regions.map((region) => (
                  <Grid item xs={12} md={6} lg={3} key={region.region_name}>
                    <GamifiedRegionCard region={region} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Gamified Districts Ranking */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  🏆 Ranking Dystryktów
                </Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="gamified">
                    🎮 Gamifikacja
                  </ToggleButton>
                  <ToggleButton value="table">
                    📊 Tabela
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {viewMode === 'gamified' ? (
                <>
                  {/* Legend */}
                  <Box sx={{ mb: 4, p: 3, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                      💡 Legenda kategorii i reguł
                    </Typography>
                    
                    {/* Badges */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                        Badge'y (odznaki) - przyznawane na podstawie wyników:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2 }}>
                        <Typography variant="body2">👑 <strong>Lider Absolutny</strong> - miejsce #1 w rankingu</Typography>
                        <Typography variant="body2">🔥 <strong>Gorący Dystrykt</strong> - miejsca #2-3 w rankingu</Typography>
                        <Typography variant="body2">⭐ <strong>Gwiazda</strong> - sprzedaż powyżej 150% średniej (wyjątkowe wyniki)</Typography>
                        <Typography variant="body2">💎 <strong>Perfekcja</strong> - sprzedaż na salon powyżej 120% średniej (efektywność)</Typography>
                        <Typography variant="body2">📈 <strong>Powyżej Średniej</strong> - sprzedaż powyżej średniej krajowej</Typography>
                        <Typography variant="body2">🎯 <strong>Solidny Gracz</strong> - sprzedaż powyżej 50% średniej (stabilne wyniki)</Typography>
                        <Typography variant="body2">💤 <strong>Potencjał</strong> - sprzedaż poniżej 50% średniej (do poprawy)</Typography>
                      </Box>
                    </Box>
                    
                    {/* Tiers */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                        Tier'y (poziomy) - pozycja względem lidera:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2 }}>
                        <Typography variant="body2">💎 <strong>Platinum</strong> - sprzedaż {'≥'} 80% wyniku lidera</Typography>
                        <Typography variant="body2">🥇 <strong>Gold</strong> - sprzedaż {'≥'} 60% wyniku lidera</Typography>
                        <Typography variant="body2">🥈 <strong>Silver</strong> - sprzedaż {'≥'} 40% wyniku lidera</Typography>
                        <Typography variant="body2">🥉 <strong>Bronze</strong> - sprzedaż {'<'} 40% wyniku lidera</Typography>
                      </Box>
                    </Box>
                    
                    {/* Tempo */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                        Tempo (dynamika) - tempo sprzedaży względem średniej:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2 }}>
                        <Typography variant="body2">🚀 <strong>Bardzo szybkie</strong> - sprzedaż {'>'} 150% średniej (wyjątkowa dynamika)</Typography>
                        <Typography variant="body2">⚡ <strong>Szybkie</strong> - sprzedaż powyżej średniej krajowej</Typography>
                        <Typography variant="body2">🏃 <strong>Umiarkowane</strong> - sprzedaż {'>'} 70% średniej (dobre tempo)</Typography>
                        <Typography variant="body2">🐢 <strong>Wolne</strong> - sprzedaż {'>'} 30% średniej (tempo do poprawy)</Typography>
                        <Typography variant="body2">💤 <strong>Bardzo wolne</strong> - sprzedaż {'≤'} 30% średniej (wymaga interwencji)</Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* TOP 3 Podium */}
                  {gamificationData.districts.length >= 3 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                        🏆 TOP 3 Dystryktów
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                          gap: 3,
                          maxWidth: '1400px',
                          mx: 'auto'
                        }}
                      >
                        {/* 1st Place */}
                        <GamifiedDistrictCard
                          district={gamificationData.districts[0]}
                          onClick={() => setExpandedDistrict(
                            expandedDistrict === gamificationData.districts[0].district_name 
                              ? null 
                              : gamificationData.districts[0].district_name
                          )}
                        />
                        
                        {/* 2nd Place */}
                        <GamifiedDistrictCard
                          district={gamificationData.districts[1]}
                          onClick={() => setExpandedDistrict(
                            expandedDistrict === gamificationData.districts[1].district_name 
                              ? null 
                              : gamificationData.districts[1].district_name
                          )}
                        />
                        
                        {/* 3rd Place */}
                        <GamifiedDistrictCard
                          district={gamificationData.districts[2]}
                          onClick={() => setExpandedDistrict(
                            expandedDistrict === gamificationData.districts[2].district_name 
                              ? null 
                              : gamificationData.districts[2].district_name
                          )}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Rest of Districts */}
                  <Typography variant="h6" gutterBottom sx={{ mt: 6, mb: 2 }}>
                    Pozostałe dystrykty
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                      gap: 2,
                      pt: 2
                    }}
                  >
                    {gamificationData.districts.slice(3).map((district) => (
                      <GamifiedDistrictCard
                        key={`${district.region_name}-${district.district_name}`}
                        district={district}
                        onClick={() => setExpandedDistrict(
                          expandedDistrict === district.district_name ? null : district.district_name
                        )}
                      />
                    ))}
                  </Box>
                </>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>Region</strong></TableCell>
                        <TableCell><strong>Dystrykt</strong></TableCell>
                        <TableCell align="right"><strong>Sprzedaż</strong></TableCell>
                        <TableCell align="right"><strong>Salony</strong></TableCell>
                        <TableCell align="right"><strong>vs Średnia</strong></TableCell>
                        <TableCell align="right"><strong>Badge</strong></TableCell>
                        {analysisMode === 'yearly' && monthNames.slice(0, 6).map((name, idx) => (
                          <TableCell key={idx} align="right" sx={{ minWidth: 70 }}>
                            <strong>{name.substring(0, 3)}</strong>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gamificationData.districts.map((district) => (
                        <TableRow 
                          key={`${district.region_name}-${district.district_name}`}
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            backgroundColor: district.rank <= 3 ? 'rgba(76, 175, 80, 0.1)' : 'inherit'
                          }}
                          onClick={() => setExpandedDistrict(expandedDistrict === district.district_name ? null : district.district_name)}
                        >
                          <TableCell>{district.medal || district.rank}</TableCell>
                          <TableCell>{district.region_name}</TableCell>
                          <TableCell>
                            <strong>{district.district_name}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={district.total_sales.toLocaleString()} 
                              color="primary" 
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">{district.salon_count}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${district.vsAverage > 0 ? '+' : ''}${district.vsAverage}%`}
                              size="small"
                              color={district.vsAverageNum >= 0 ? 'success' : 'error'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={district.badge.label}>
                              <span style={{ fontSize: '1.2rem' }}>{district.badge.icon}</span>
                            </Tooltip>
                          </TableCell>
                          {analysisMode === 'yearly' && monthNames.slice(0, 6).map((_, idx) => (
                            <TableCell key={idx} align="right">
                              {district.monthly_sales?.[idx + 1] || 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                💡 <strong>Legenda:</strong> 👑 Lider | 🔥 Gorący dystrykt | ⭐ Gwiazda | 💎 Perfekcja | 📈 Powyżej średniej | 🎯 Solidny gracz | 💤 Potencjał
              </Alert>
            </CardContent>
          </Card>

          {/* Regional Hierarchical View */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📍 Widok hierarchiczny: Regiony → Dystrykty → Salony
              </Typography>
              
              {data.regions.map((region) => (
                <Accordion key={region.region_name} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography sx={{ width: '40%', flexShrink: 0 }}>
                        <strong>{region.region_name}</strong>
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', mr: 2 }}>
                        Dystryktów: {region.district_count}
                      </Typography>
                      <Chip 
                        label={`${region.total_sales.toLocaleString()} szt.`} 
                        color="primary" 
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {region.districts.map((district) => (
                      <Accordion key={district.district_name} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Typography sx={{ width: '40%', flexShrink: 0 }}>
                              {district.district_name}
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', mr: 2 }}>
                              Salonów: {district.salon_count}
                            </Typography>
                            <Chip 
                              label={`${district.total_sales.toLocaleString()} szt.`} 
                              color="secondary" 
                              size="small"
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer component={Paper}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Salon</strong></TableCell>
                                  <TableCell><strong>Plant Code</strong></TableCell>
                                  <TableCell align="right"><strong>Sprzedaż</strong></TableCell>
                                  {analysisMode === 'yearly' && monthNames.slice(0, 4).map((name, idx) => (
                                    <TableCell key={idx} align="right">
                                      <strong>{name.substring(0, 3)}</strong>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {district.salons.map((salon) => (
                                  <TableRow key={salon.plant_code}>
                                    <TableCell>{salon.salon_name}</TableCell>
                                    <TableCell>{salon.plant_code}</TableCell>
                                    <TableCell align="right">
                                      <strong>{salon.total_sales.toLocaleString()}</strong>
                                    </TableCell>
                                    {analysisMode === 'yearly' && monthNames.slice(0, 4).map((_, idx) => (
                                      <TableCell key={idx} align="right">
                                        {salon.monthly_sales?.[idx + 1] || 0}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>

          {/* Charts Section */}
          <Box sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="🏆 TOP 20 Dystryktów" />
              <Tab label="📊 Porównanie regionów" />
            </Tabs>

            <Box sx={{ mt: 2 }}>
              {activeTab === 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      TOP 20 Dystryktów według sprzedaży
                    </Typography>
                    
                    <Box sx={{ height: 600 }}>
                      {chartData.topDistricts.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart 
                            data={chartData.topDistricts} 
                            margin={{ top: 20, right: 50, left: 20, bottom: 150 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name" 
                              angle={-45}
                              textAnchor="end"
                              height={120}
                              interval={0}
                              tick={{ fontSize: 10 }}
                            />
                            <YAxis />
                            <RechartsTooltip 
                              formatter={(value) => [`${value} szt.`, 'Sprzedaż']}
                              labelFormatter={(label, payload) => {
                                const data = payload?.[0]?.payload
                                return data ? `${data.fullName} (${data.region})` : label
                              }}
                            />
                            <Bar dataKey="sales" fill="#ff9800" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Alert severity="warning">
                          Brak danych do wyświetlenia wykresu TOP 20
                        </Alert>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {activeTab === 1 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Porównanie sprzedaży między regionami
                    </Typography>
                    
                    <Box sx={{ height: 400 }}>
                      {chartData.regionalComparison.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart 
                            data={chartData.regionalComparison}
                            margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="region" />
                            <YAxis />
                            <RechartsTooltip 
                              formatter={(value, name) => [
                                name === 'sales' ? `${value} szt.` : value,
                                name === 'sales' ? 'Sprzedaż' : 'Dystryktów'
                              ]}
                            />
                            <Bar dataKey="sales" fill="#4caf50">
                              {chartData.regionalComparison.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Alert severity="warning">
                          Brak danych do wyświetlenia porównania regionalnego
                        </Alert>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        </>
      )}
    </ReportShell>
  )
}

export default OrangeDistrictAnalysis

